import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { UserGuard } from 'src/auth-guard/user-guard';
import { LoadingScreen } from 'src/components/loading-screen';
import AdminLayout from 'src/layouts/dashboard';
const DashboardPage = lazy(() => import('src/pages/dashboard/dashboard-page'));

const MemberPage = lazy(() => import('src/pages/member/member-page'));
const MemberDetailPage = lazy(() => import('src/pages/member/[id]/member-detail-page'));

const PaymentAppPage = lazy(() => import('src/pages/payment/app/payment-app-page'));
const PaymentCodePage = lazy(() => import('src/pages/payment/code/payment-code-page'));

const CodeFirstLevelPage = lazy(() => import('src/pages/code/first-level/code-first-level-page'));
const CodeSecondLevelPage = lazy(() => import('src/pages/code/second-level/code-second-level-page'));

const ExamFirstLevelPage = lazy(() => import('src/pages/exam/first-level/exam-first-level-page'));
const ExamFirstLevelDetailPage = lazy(() => import('src/pages/exam/first-level/[id]/exam-first-level-exam-list-page'));
const ExamFirstLevelAddPage = lazy(() => import('src/pages/exam/first-level/[id]/add/first-level-exam-add-page'));
const ExamFirstLevelExamDetailPage = lazy(
  () => import('src/pages/exam/first-level/exam/[id]/first-level-exam-detail-page')
);

const ExamSecondLevelPage = lazy(() => import('src/pages/exam/second-level/exam-second-level-page'));
const ExamSecondLevelDetailPage = lazy(
  () => import('src/pages/exam/second-level/[id]/exam-second-level-exam-list-page')
);
const ExamSecondLevelAddPage = lazy(() => import('src/pages/exam/second-level/[id]/add/second-level-exam-add-page'));
const ExamSecondLevelExamDetailPage = lazy(
  () => import('src/pages/exam/second-level/exam/[id]/second-level-exam-detail-page')
);

const StatisticsPage = lazy(() => import('src/pages/statistics/statistics-page'));

const AdminAccountPage = lazy(() => import('src/pages/admin-account/admin-account-page'));

const TermsPage = lazy(() => import('src/pages/terms/terms-page'));

const WebAdminDashboardPage = lazy(() => import('src/pages/web-admin/web-admin-dashboard-page'));
const WebAdminMembersPage = lazy(() => import('src/pages/web-admin/web-admin-members-page'));
const WebAdminMemberDetailPage = lazy(() => import('src/pages/web-admin/web-admin-member-detail-page'));
const WebAdminCodesPage = lazy(() => import('src/pages/web-admin/web-admin-codes-page'));
const WebAdminCodeRegistrationsPage = lazy(() => import('src/pages/web-admin/web-admin-code-registrations-page'));
const WebAdminDownloadsPage = lazy(() => import('src/pages/web-admin/web-admin-downloads-page'));
const WebAdminWatermarkPage = lazy(() => import('src/pages/web-admin/web-admin-watermark-page'));
const WebAdminLecturesPage = lazy(() => import('src/pages/web-admin/web-admin-lectures-page'));
const WebAdminLectureFormPage = lazy(() => import('src/pages/web-admin/web-admin-lecture-form-page'));
const WebAdminTextbooksPage = lazy(() => import('src/pages/web-admin/web-admin-textbooks-page'));
const WebAdminTextbookFormPage = lazy(() => import('src/pages/web-admin/web-admin-textbook-form-page'));
const WebAdminStatsPage = lazy(() => import('src/pages/web-admin/web-admin-stats-page'));
const WebAdminTermsPage = lazy(() => import('src/pages/web-admin/web-admin-terms-page'));
const WebAdminCustomerCenterPage = lazy(() => import('src/pages/web-admin/web-admin-customer-center-page'));
const WebAdminCustomerCenterFormPage = lazy(
  () => import('src/pages/web-admin/web-admin-customer-center-form-page')
);
const WebAdminRevisionsPage = lazy(() => import('src/pages/web-admin/web-admin-revisions-page'));
const WebAdminRevisionFormPage = lazy(() => import('src/pages/web-admin/web-admin-revision-form-page'));
const WebAdminContentPage = lazy(() => import('src/pages/web-admin/web-admin-content-page'));
const WebAdminMaterialsPage = lazy(() => import('src/pages/web-admin/web-admin-materials-page'));
const WebAdminMaterialFormPage = lazy(() => import('src/pages/web-admin/web-admin-material-form-page'));

// TODO: 임시 페이지 삭제 필요
const TempPage = lazy(() => import('src/pages/temp/temp-page'));
// ----------------------------------------------------------------------

export const loginRequiredRoutes = [
  {
    path: '',
    element: (
      <UserGuard>
        <AdminLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </AdminLayout>
      </UserGuard>
    ),
    children: [
      { element: <DashboardPage />, index: true },
      { path: 'dashboard', element: <DashboardPage /> },

      {
        path: 'web-admin',
        children: [
          { element: <WebAdminDashboardPage />, index: true },
          { path: 'dashboard', element: <WebAdminDashboardPage /> },
          { path: 'members', element: <WebAdminMembersPage /> },
          { path: 'members/:id', element: <WebAdminMemberDetailPage /> },
          { path: 'code-registrations', element: <WebAdminCodeRegistrationsPage /> },
          { path: 'downloads', element: <WebAdminDownloadsPage /> },
          { path: 'watermark', element: <WebAdminWatermarkPage /> },
          { path: 'stats', element: <WebAdminStatsPage /> },
          { path: 'terms', element: <WebAdminTermsPage /> },
          { path: 'customer-center/:resource/new', element: <WebAdminCustomerCenterFormPage /> },
          { path: 'customer-center/:resource/:id', element: <WebAdminCustomerCenterFormPage /> },
          { path: 'customer-center/:resource', element: <WebAdminCustomerCenterPage /> },
          { path: 'content/revisions', element: <WebAdminRevisionsPage /> },
          { path: 'content/revisions/new', element: <WebAdminRevisionFormPage /> },
          { path: 'content/revisions/:id', element: <WebAdminRevisionFormPage /> },
          { path: 'content/materials', element: <WebAdminMaterialsPage /> },
          { path: 'content/materials/new', element: <WebAdminMaterialFormPage /> },
          { path: 'content/materials/:id', element: <WebAdminMaterialFormPage /> },
          { path: 'content/lectures', element: <WebAdminLecturesPage /> },
          { path: 'content/lectures/new', element: <WebAdminLectureFormPage /> },
          { path: 'content/lectures/:id', element: <WebAdminLectureFormPage /> },
          { path: 'content/textbooks', element: <WebAdminTextbooksPage /> },
          { path: 'content/textbooks/new', element: <WebAdminTextbookFormPage /> },
          { path: 'content/textbooks/:id', element: <WebAdminTextbookFormPage /> },
          { path: 'content/:resource', element: <WebAdminContentPage /> }
        ]
      },

      {
        path: 'member',
        children: [
          { element: <MemberPage />, index: true },
          { path: ':id', element: <MemberDetailPage /> }
        ]
      },
      {
        path: 'payment',
        children: [
          { element: <PaymentAppPage />, index: true },
          { path: 'app', element: <PaymentAppPage /> },
          { path: 'code', element: <PaymentCodePage /> }
        ]
      },
      {
        path: 'code',
        children: [
          { element: <Navigate replace to="web/grade1-written" />, index: true },
          { path: 'first-level', element: <Navigate replace to="../web/grade1-written" /> },
          { path: 'second-level', element: <Navigate replace to="../web/grade2-written" /> },
          { path: 'web/:productType', element: <WebAdminCodesPage /> }
        ]
      },
      {
        path: 'app-code',
        children: [
          { element: <Navigate replace to="first-level" />, index: true },
          { path: 'first-level', element: <CodeFirstLevelPage /> },
          { path: 'second-level', element: <CodeSecondLevelPage /> }
        ]
      },
      {
        path: 'exam',
        children: [
          { element: <ExamFirstLevelPage />, index: true },
          {
            path: 'first-level',
            element: <ExamFirstLevelPage />
          },
          {
            path: 'first-level/:id',
            element: <ExamFirstLevelDetailPage />
          },
          {
            path: 'first-level/:id/add',
            element: <ExamFirstLevelAddPage />
          },
          {
            path: 'first-level/exam/:id',
            element: <ExamFirstLevelExamDetailPage />
          },
          {
            path: 'second-level',
            element: <ExamSecondLevelPage />
          },
          {
            path: 'second-level/:id',
            element: <ExamSecondLevelDetailPage />
          },
          {
            path: 'second-level/:id/add',
            element: <ExamSecondLevelAddPage />
          },
          {
            path: 'second-level/exam/:id',
            element: <ExamSecondLevelExamDetailPage />
          }
        ]
      },
      {
        path: 'statistics',
        element: <StatisticsPage />
      },

      {
        path: 'terms',
        element: <TermsPage />
      },
      {
        path: 'admin-account',
        children: [{ element: <AdminAccountPage />, index: true }]
      },
      // TODO: 임시 페이지 삭제 필요
      {
        path: 'temp',
        children: [{ element: <TempPage />, index: true }]
      }
    ]
  }
];
